import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { Form, InputNumber } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import { FeeCreatePayload } from "../../../types/feeTypes";
import { FeeType } from "types/feeTypeTypes";
import { StudentType } from "types/studentTypes";
import { formatMoney } from "helpers/numberHelper";
import CommonSelect, { Option as SelectOption } from "../../../components/Common/CommonSelect";
import CommonMultiSelect from "components/Common/CommonMultiSelect";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { getClassLevelLabelFromCode } from "utils/schoolUtils";
import { ClassData, StudentData } from "types/classTypes";

interface AddClassFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FeeCreatePayload & { studentIds?: string[] }) => Promise<boolean>;
  feeTypes: FeeType[];
  classes: ClassData[];
  students: StudentData[]; // ✅ NEW
  selectedClass?: string;
}

const AddClassFeeModal: React.FC<AddClassFeeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  feeTypes,
  classes,
  students,
  selectedClass,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedFeeType, setSelectedFeeType] = useState<FeeType | null>(null);
  const [selectedClassLevelId, setSelectedClassLevelId] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null); // ✅ NEW

  const monthsOptions = useMemo(
    () => [
      { value: 0, label: t("list_of_months.January") },
      { value: 1, label: t("list_of_months.February") },
      { value: 2, label: t("list_of_months.March") },
      { value: 3, label: t("list_of_months.April") },
      { value: 4, label: t("list_of_months.May") },
      { value: 5, label: t("list_of_months.June") },
      { value: 6, label: t("list_of_months.July") },
      { value: 7, label: t("list_of_months.August") },
      { value: 8, label: t("list_of_months.September") },
      { value: 9, label: t("list_of_months.October") },
      { value: 10, label: t("list_of_months.November") },
      { value: 11, label: t("list_of_months.December") },
    ],
    [t]
  );

  const computeAmount = (feeType: FeeType | null, classLevelId?: string | null) => {
    if (!feeType) return undefined;

    if (feeType.pricingModel === "unique_payment") {
      return Number(feeType.baseAmount ?? 0);
    }

    if (feeType.pricingModel === "level_based_payment") {
      if (classLevelId) {
        const found = feeType.feePricing?.find((p) => p.classLevelId === classLevelId);
        if (found) return Number(found.amount ?? 0);
        return undefined;
      }
      return undefined;
    }

    return undefined;
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const { classId, studentIds, feePricingId, dueDate, dueMonths, ...rest } = values;

      const payload = {
        ...rest,
        classId,
        ...(Array.isArray(studentIds) ? { studentIds } : {}),
        ...(dueDate ? { dueDate: dueDate.format("MM-DD-YYYY") } : {}),
        ...(Array.isArray(dueMonths) ? { dueMonths } : {}),
      };

      const result = await onSave(payload);
      if (result) {
        onClose();
        form.resetFields();
        setSelectedFeeType(null);
        setSelectedClassLevelId(null);
        setSelectedClassId(null);
      }
    } catch (err) {
      console.error("Error saving class fee:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (option: SelectOption | null) => {
    const classId = option?.value || null;
    form.setFieldValue("classId", classId);
    setSelectedClassId(classId);

    const klass = classes.find((c) => c.id === classId);
    const newClassLevelId = klass?.classLevel?.id ?? null;
    setSelectedClassLevelId(newClassLevelId);

    const newAmount = computeAmount(selectedFeeType, newClassLevelId);
    form.setFieldsValue({ amount: newAmount });

    if (classId) {
      const ids = students.filter((s) => s?.classId === classId).map((s) => s.id);
      form.setFieldValue("studentIds", ids);
    } else {
      form.setFieldValue("studentIds", []);
    }
  };

  const handleFeeTypeChange = (option: SelectOption | null) => {
    const feeTypeId = option?.value || null;
    const newFeeType = feeTypes.find((ft) => ft.id === feeTypeId) || null;
    setSelectedFeeType(newFeeType);
    form.setFieldValue("feeTypeId", feeTypeId);
    form.setFieldsValue({ feePricingId: undefined });

    const newAmount = computeAmount(newFeeType, selectedClassLevelId);
    form.setFieldsValue({ amount: newAmount });
  };

  const handleFeePricingChange = (option: SelectOption | null) => {
    if (!selectedFeeType) return;
    const selectedPricing = selectedFeeType.feePricing?.find((p) => p.id === option?.value);
    form.setFieldsValue({
      feePricingId: option?.value || null,
      amount: Number(selectedPricing?.amount ?? 0),
    });
  };

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedFeeType(null);
      setSelectedClassLevelId(null);
      setSelectedClassId(null);

      if (selectedClass) {
        form.setFieldValue("classId", selectedClass);
        setSelectedClassId(selectedClass);

        const klass = classes.find((c) => c.id === selectedClass);
        const classLevelId = klass?.classLevel?.id ?? null;
        setSelectedClassLevelId(classLevelId);
        const ids = students.filter((s) => s?.classId === selectedClass).map((s) => s.id);
        form.setFieldValue("studentIds", ids);
      }
    }
  }, [isOpen, selectedClass, form, classes, students]);

  useEffect(() => {
    const amount = computeAmount(selectedFeeType, selectedClassLevelId);
    if (amount !== undefined) form.setFieldsValue({ amount });
  }, [selectedFeeType, selectedClassLevelId, form]);

  const classOptions: SelectOption[] = useMemo(
    () =>
      classes.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [classes]
  );

  const feeTypeOptions: SelectOption[] = useMemo(
    () => feeTypes.map((ft) => ({ value: ft.id, label: ft.name })),
    [feeTypes]
  );

  const classStudentOptions: SelectOption[] = useMemo(() => {
    if (!selectedClassId) return [];
    return students
      .filter((s) => s?.classId === selectedClassId)
      .map((s) => ({
        value: s.id,
        label: `${s.user.firstName} ${s.user.lastName}`,
      }));
  }, [students, selectedClassId]);

  const feePricingOptions: SelectOption[] = useMemo(() => {
    if (!selectedFeeType?.feePricing) return [];
    return selectedFeeType.feePricing.map((p) => ({
      value: p.id,
      label: `${getClassLevelLabelFromCode(p.classLevel?.labelKey ?? "")} - ${formatMoney(Number(p.amount ?? 0))}`,
    }));
  }, [selectedFeeType]);

  return (
    <CustomModal
      title={t("add_fee_to_class")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={t("save")}
    >
      <Form form={form} layout="vertical">
        {selectedClass ? (
          <Form.Item name="classId" hidden initialValue={selectedClass}>
            <input type="hidden" />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("class")}
            name="classId"
            rules={[{ required: true, message: t("validation.please_select_class") }]}
          >
            <CommonSelect options={classOptions} onChange={handleClassChange} />
          </Form.Item>
        )}

        {selectedClassId && (
          <Form.Item
            label={t("students_list")}
            name="studentIds"
            rules={[{ required: true, message: t("validation.please_select_student") }]}
          >
            <CommonMultiSelect
              options={classStudentOptions}
              placeholder={t("students_list")}
              value={form.getFieldValue("studentIds")}
              onChange={(ids: string[]) => form.setFieldValue("studentIds", ids)}
            />
          </Form.Item>
        )}

        <Form.Item
          label={t("fee_type")}
          name="feeTypeId"
          rules={[{ required: true, message: t("validation.please_select_fee_type") }]}
        >
          <CommonSelect options={feeTypeOptions} onChange={handleFeeTypeChange} />
        </Form.Item>

        {selectedFeeType?.pricingModel === "level_based_payment" && !selectedClassLevelId && (
          <Form.Item
            label={t("pricing_type")}
            name="feePricingId"
            rules={[{ required: true, message: t("validation.please_select_fee_pricing") }]}
          >
            <CommonSelect options={feePricingOptions} onChange={handleFeePricingChange} />
          </Form.Item>
        )}

        <Form.Item
          label={t("amount")}
          name="amount"
          rules={[{ required: true, message: t("validation.please_enter_amount") }]}
        >
          <InputNumber
            className="w-100"
            placeholder={t("amount")}
            min={0}
            precision={2}
            addonAfter="MAD"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          />
        </Form.Item>

        {selectedFeeType?.frequency === "unique_payment" ? (
          <Form.Item
            label={t("due_date")}
            name="dueDate"
            rules={[{ required: true, message: t("validation.please_select_due_date") }]}
          >
            <LocalizedDatePicker className="w-100" />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("select_months")}
            name="dueMonths"
            rules={[{ required: true, message: t("validation.please_select_months") }]}
          >
            <CommonMultiSelect
              options={monthsOptions}
              placeholder={t("select_months")}
              value={form.getFieldValue("dueMonths")}
              onChange={(value: number[]) => form.setFieldValue("dueMonths", value)}
            />
          </Form.Item>
        )}
      </Form>
    </CustomModal>
  );
};

export default AddClassFeeModal;
